/**
 * ParagraphWithAuthor — V2.5 Block-Level Authorship + block_id-dedup.
 *
 * V2.5 (2026-05-21) adds a fresh-UUID re-stamp on block_id collisions
 * during local typing. Without this, ProseMirror's Enter-split copies
 * the parent paragraph's `block_id` into the new child paragraph —
 * mirroring the V2.0/V2.3 author_id bug but on a different attribute.
 *
 * Why it matters: the canvas-engine LayoutEngine.sync(items, idOf)
 * deduplicates blocks by `block_id`. Two paragraphs sharing the same
 * id collapse into ONE LayoutNode on the canvas, so the user sees
 * fewer cards than the doc actually has. Repeated "delete card" clicks
 * on the visible card then remove all hidden duplicates → silent
 * wipe of multiple paragraphs (Post #797 incident 2026-05-21 16:06).
 *
 * Fix: extend the auto-restamp plugin so that for each local doc-
 * changing transaction, we walk paragraphs in order and assign a fresh
 * crypto.randomUUID() to any paragraph whose block_id is already used
 * by an earlier paragraph. First occurrence keeps the id, later ones
 * get fresh ones. The next chunk_committed cycle promotes the fresh
 * client-side id into the server's lazy-on-write path.
 *
 * --- V2.4 history (still applies): ---
 * V2.4 added two paragraph attributes that mirror Phase-1 server-side
 * fields:
 *   - block_id: per-paragraph UUID (server-generated on first commit)
 *   - block_type: enum string (doc_content | chat_response | annotation
 *                              | question | synthesis | system_note | media)
 *
 * Both arrive from the server via initial doc load and chunk_committed
 * cascade; the editor parses them from data-* attrs on initial parse
 * and emits them on serialize. block_type is NOT re-stamped on local
 * typing — server-side heuristic owns it on the next REST write, and
 * default 'doc_content' is correct for client-created paragraphs.
 *
 * --- V2.3 history (still applies): ---
 * V2.2 was paragraph attrs only and had a bug: pressing Enter at the end
 * of an agent's paragraph copied parent attrs to the new paragraph so the
 * user's typing inherited the agent's color. V2.3 adds a ProseMirror
 * plugin that watches local browser transactions (skipping incoming
 * y-sync) and re-stamps any paragraph the local user actively typed into.
 *
 * Safety guards (learned from V2.0's wipe-bug):
 *   - Skip transactions tagged by y-sync (incoming remote inserts don't
 *     trigger our local re-stamp)
 *   - Skip our own re-stamp transactions (tagged via meta)
 *   - Operate at Node-attribute level, not Mark level — Yjs node
 *     attributes are CRDT-stable and don't compete with text-merge
 *
 * The plugin needs the local user identity at construction time. If
 * `user` is null (read-only context), the plugin is a no-op — read-
 * only viewers don't issue local doc changes so neither author-
 * restamp nor block_id-dedup has anything to act on. Polluted docs
 * from pre-V2.5 writes are cleaned up on the next editor's first
 * local edit.
 */

import Paragraph from '@tiptap/extension-paragraph'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { ySyncPluginKey } from '@tiptap/y-tiptap'

export interface ParagraphAuthorUser {
  id: string
  name: string
  color: string
}

interface ParagraphWithAuthorOptions {
  HTMLAttributes: Record<string, unknown>
  /**
   * Local user identity for auto-reattribution. Pass `null` in
   * read-only contexts to disable the auto-stamp plugin.
   */
  user: ParagraphAuthorUser | null
}

const RESTAMP_META = 'paragraphAuthor:restamp'
const REATTRIBUTE_PLUGIN_KEY = new PluginKey('paragraphAuthorAutoReattribute')

export const ParagraphWithAuthor = Paragraph.extend<ParagraphWithAuthorOptions>({
  addOptions() {
    return {
      HTMLAttributes: {},
      user: null,
    }
  },

  addAttributes() {
    return {
      ...(this.parent?.() || {}),
      author_id: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-author-id'),
        renderHTML: (attrs) =>
          attrs.author_id
            ? { 'data-author-id': attrs.author_id as string }
            : {},
      },
      author_name: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-author-name'),
        renderHTML: (attrs) =>
          attrs.author_name
            ? { 'data-author-name': attrs.author_name as string }
            : {},
      },
      author_color: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-author-color'),
        renderHTML: (attrs) =>
          attrs.author_color
            ? { 'data-author-color': attrs.author_color as string }
            : {},
      },
      block_id: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-block-id'),
        renderHTML: (attrs) =>
          attrs.block_id
            ? { 'data-block-id': attrs.block_id as string }
            : {},
      },
      block_type: {
        // Default 'doc_content' — server's auto-heuristic runs on REST
        // writes only, not on y-update writes (e.g. Enter in editor).
        // Locally-created paragraphs need this default so they reach the
        // server with a valid block_type instead of an empty attr.
        default: 'doc_content',
        parseHTML: (el) => el.getAttribute('data-block-type') || 'doc_content',
        renderHTML: (attrs) =>
          attrs.block_type
            ? { 'data-block-type': attrs.block_type as string }
            : {},
      },
      // Issue #10: server-stamped paragraph birth time (ISO 8601 UTC).
      // Read-only on the client — only the server's `_add_paragraph_at`
      // and seed paths stamp it, never the editor. The TipTap re-stamp
      // plugin below intentionally ignores it. Null on legacy paragraphs
      // written before the stamping deploy; consumers (canvas-engine
      // TimelineLayouter) fall back to paragraph_index.
      created_at: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-created-at'),
        renderHTML: (attrs) =>
          attrs.created_at
            ? { 'data-created-at': attrs.created_at as string }
            : {},
      },
      // Issue #16: shared identifier across every paragraph produced
      // by a single REST `doc_apply_text` call. Null on WS-typed and
      // pre-deploy paragraphs (each its own group). Read-only, like
      // created_at — server is the only author.
      commit_id: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-commit-id'),
        renderHTML: (attrs) =>
          attrs.commit_id
            ? { 'data-commit-id': attrs.commit_id as string }
            : {},
      },
      // V2.3 Word-Level Authorship (Content-API deployed 2026-06-22,
      // Task #164/#167). JSON-string of authorship_runs covering the
      // paragraph's plain text 0..len lückenlos:
      // [{ start, end, author_id, author_name, author_color,
      //    commit_id, source }, ...]
      // Offsets are Unicode code-point offsets in the paragraph's
      // materialised plain text. Server sidecar — read-only attr on
      // the client; the WordLevelAuthorship extension parses + renders
      // it as inline Decorations (subtle background tint per run).
      authorship_runs: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-authorship-runs'),
        renderHTML: (attrs) =>
          attrs.authorship_runs
            ? { 'data-authorship-runs': attrs.authorship_runs as string }
            : {},
      },
    }
  },

  addProseMirrorPlugins() {
    const user = this.options.user
    if (!user || !user.id) return []

    return [
      new Plugin({
        key: REATTRIBUTE_PLUGIN_KEY,
        appendTransaction: (transactions, oldState, newState) => {
          // Only act on local doc-changing transactions (skip incoming
          // y-sync remote applies and our own re-stamp ticks).
          const hasLocalChange = transactions.some(
            (tr) =>
              tr.docChanged &&
              !tr.getMeta(ySyncPluginKey) &&
              !tr.getMeta(RESTAMP_META) &&
              !tr.getMeta('y-sync$')
          )
          if (!hasLocalChange) return null

          // Compose a single Mapping covering ALL transactions so we can
          // map new positions back to old-doc positions in one shot.
          // ProseMirror's `tr.mapping` mutates as steps are appended, so
          // we compose explicitly here.
          //
          // For each paragraph in the NEW doc:
          //   1. Map its start position BACK to the old doc
          //   2. If the mapped position has the same paragraph there →
          //      compute text-delta between old and new versions of THIS
          //      paragraph. If chars added > 0, re-attribute.
          //   3. If no matching old paragraph (newly created via split) →
          //      re-attribute only if the new paragraph has text content
          //      (don't re-attribute an empty paragraph right after a
          //      split — wait for the user to actually type something).
          //
          // This fixes the bug where pressing Enter at the end of an
          // agent's paragraph re-attributed that paragraph to the local
          // user (because both pre-split and post-split paragraphs had
          // overlapping insert-ranges). Text-delta-based detection
          // correctly ignores pure structural changes.
          const tr = newState.tr
          let mutated = false

          const paragraphTextOf = (node: { textContent?: string }): string =>
            (node.textContent || '').trim()

          // V2.5: track block_ids seen so far so duplicate occurrences
          // (caused by Enter-splits inheriting parent's attrs) get a
          // fresh UUID. First paragraph keeps its id, later ones with
          // the same id get re-stamped.
          const seenBlockIds = new Set<string>()

          newState.doc.descendants((newNode, newPos) => {
            if (newNode.type.name !== 'paragraph') return true
            const attrs = newNode.attrs as {
              author_id?: string | null
              author_name?: string | null
              author_color?: string | null
              block_id?: string | null
            }

            // --- V2.5 block_id dedup ---
            // Run BEFORE author-restamp so the new markup (if any)
            // includes the deduped block_id.
            let nextBlockId = attrs.block_id ?? null
            if (nextBlockId) {
              if (seenBlockIds.has(nextBlockId)) {
                // Collision — generate fresh id. crypto.randomUUID()
                // is supported in all browsers we target (Chrome 92+,
                // Safari 15.4+, Firefox 95+).
                nextBlockId = crypto.randomUUID()
              }
              seenBlockIds.add(nextBlockId)
            }
            const blockIdChanged = nextBlockId !== (attrs.block_id ?? null)

            // --- V2.3 author-restamp ---
            // Already correctly attributed → only run block_id-dedup
            // if needed, otherwise skip.
            if (attrs.author_id === user.id) {
              if (blockIdChanged) {
                tr.setNodeMarkup(newPos, undefined, {
                  ...newNode.attrs,
                  block_id: nextBlockId,
                })
                mutated = true
              }
              return true
            }

            const newText = paragraphTextOf(newNode as { textContent?: string })

            // Find the corresponding paragraph in the old doc (if any).
            // We map the new paragraph's start position back through all
            // transactions. If the mapped old position lands on a
            // paragraph node, that's its predecessor.
            let oldText: string | null = null
            try {
              // Map backward through each transaction's mapping in reverse.
              let oldPos = newPos
              for (let i = transactions.length - 1; i >= 0; i--) {
                oldPos = transactions[i].mapping.invert().map(oldPos)
              }
              if (oldPos >= 0 && oldPos < oldState.doc.content.size) {
                const oldNode = oldState.doc.nodeAt(oldPos)
                if (oldNode && oldNode.type.name === 'paragraph') {
                  oldText = paragraphTextOf(oldNode as { textContent?: string })
                }
              }
            } catch {
              oldText = null
            }

            let shouldRestampAuthor = true
            if (oldText === null) {
              // Newly created paragraph (e.g. result of an Enter-split
              // with no preimage). Only re-attribute if it has text.
              if (newText.length === 0) shouldRestampAuthor = false
            } else {
              // Pre-existing paragraph. Re-attribute ONLY if there's a
              // real text delta — i.e. the user typed something into it,
              // not just a structural change like split-shift.
              if (newText === oldText) shouldRestampAuthor = false
              // If both have content and they differ, check that NEW
              // content has MORE characters (i.e. user added text). A
              // pure split shortens the original; that should NOT re-
              // attribute. A pure delete also shouldn't.
              else if (newText.length <= oldText.length) shouldRestampAuthor = false
            }

            // If neither author-restamp nor block_id-change is needed,
            // skip the setNodeMarkup call entirely.
            if (!shouldRestampAuthor && !blockIdChanged) return true

            const patch: Record<string, unknown> = { ...newNode.attrs }
            if (shouldRestampAuthor) {
              patch.author_id = user.id
              patch.author_name = user.name
              patch.author_color = user.color
            }
            if (blockIdChanged) {
              patch.block_id = nextBlockId
            }
            tr.setNodeMarkup(newPos, undefined, patch)
            mutated = true
            return true
          })

          if (!mutated) return null
          tr.setMeta(RESTAMP_META, true)
          return tr
        },
      }),
    ]
  },
})
